import { createSelector } from 'reselect';
import isEqual from 'lodash/isEqual';
import { format, isAfter, isBefore, isSameDay } from 'date-fns';
import { assertUnreachable } from 'utils/types';
import { Tier } from 'features/premium/entities';
import { createNonMemoizedSelector } from 'store/selectors';
import { addLastInSectionInfo } from 'utils/getTransactions';
import { selectUserTier } from 'features/premium/selectors/tier';
import { formatAmount, formatDateForSectionHeader } from 'utils/formatting';
import checkFeatureFlag from 'utils/featureFlags';
import { getActivitiesSectionedFlashlist as getActivitiesSectionedFlashlistFromUtils, addFlashListSectionedDataToActivities, } from 'utils/formatting/activities';
import { selectFeatureFlag, selectIsGBUser } from 'reducers/selectors';
import { isWithinNext24Hours } from 'utils/dates';
import { SellOrderType, AssetFilterCategory, } from '../types';
import * as debug from '../debug';
import { ALL_STOCKS_KEY, fundThemeInfoMap, fundGeoInfoMap, MINIMUM_AUTOINVEST_AMOUNT } from '../constants';
import { formatNumberOfShares, getAlpacaAccount, getHasAccountsHeldElsewhere, getSecclGiaAccount, getSecclISAAccount, getSecclJISAAccounts, } from '../utils';
import { filterDataByKey } from '../utils/format';
import { getAutoInvestDisplayStatus } from '../utils/autoinvest';
import { isWeb } from '../../../constants';
const selectSecclOnlyInvestAccounts = createSelector([(store) => store.invest.accounts], (accounts) => accounts?.filter((account) => account.account.broker !== 'alpaca'));
/**
 * The customer's investment accounts. Alpaca accounts are dropped on web, which
 * only supports Seccl, so no selector built on this ever picks one there.
 */
export const selectInvestAccounts = (store) => isWeb ? selectSecclOnlyInvestAccounts(store) : store.invest.accounts;
export const selectShowSecclFlow = createSelector([(store) => store.utils.featureFlags], (featureFlags) => checkFeatureFlag(featureFlags, 'seccl_onboarding_v3'));
export const selectShowInvestIntroFlag = createSelector([(store) => store.utils.featureFlags], (featureFlags) => checkFeatureFlag(featureFlags, 'start_screen_emma_invest_apr2026'));
export const selectShowInvestIntro = createSelector([selectShowInvestIntroFlag, selectInvestAccounts], (isFlagOn, accounts) => isFlagOn && !accounts?.some((account) => account.account.status !== 'NOT_STARTED'));
export const selectBrokerToOnboardInvest = createSelector([selectShowSecclFlow], (showSecclFlow) => showSecclFlow ? 'seccl' : 'alpaca');
export const selectShowMultiAccountTradingView = createSelector([selectInvestAccounts, (store) => store.invest.hasCompletedOnboardingFunnel], (accounts, hasCompletedOnboardingFunnel) => {
    if (accounts) {
        for (const account of accounts) {
            if (account.account.status === 'CLOSED' ||
                account.account.status === 'JISA_TRANSFER_REQUIRED' ||
                account.account.status === 'JISA_CONVERTED') {
                return true;
            }
            const hasAccountsHeldElsewhere = getHasAccountsHeldElsewhere(account.account);
            if (account.account.status === 'ACTIVE' && !hasAccountsHeldElsewhere) {
                if (account.account.broker === 'alpaca') {
                    return true;
                }
                if (account.hasPostOpenInstructions ||
                    account.hasTraded ||
                    hasCompletedOnboardingFunnel ||
                    ('child' in account.account && account.account.child.hasPriorJisa)) {
                    return true;
                }
            }
        }
    }
    return false;
});
export const selectWalkthroughAccount = createSelector([selectShowSecclFlow, selectInvestAccounts, (store) => store.invest.hasCompletedOnboardingFunnel], (showSecclFlow, accounts, hasCompletedOnboardingFunnel) => {
    if (!showSecclFlow)
        return undefined;
    return accounts?.find((account) => account.account.broker === 'seccl' &&
        account.account.status === 'ACTIVE' &&
        (hasCompletedOnboardingFunnel || account.hasPostOpenInstructions || account.hasTraded));
});
export const selectTradingAccount = createSelector([
    selectInvestAccounts,
    (state) => state.unpersistedInvestReducer.selectedTradingAccountId,
    selectShowSecclFlow,
], (tradingAccounts, selectedTradingAccountId, showSecclFlow) => {
    if (selectedTradingAccountId) {
        const account = tradingAccounts?.find((account) => account.account.id === selectedTradingAccountId);
        return account ? { ...account, isAlpaca: account.account.broker === 'alpaca' } : undefined;
    }
    if (!showSecclFlow) {
        const account = getAlpacaAccount(tradingAccounts);
        return account ? { ...account, isAlpaca: account.account.broker === 'alpaca' } : undefined;
    }
    return undefined;
});
export const selectOnboardingTradingAccountForOnboardingFunnel = createSelector([selectInvestAccounts], (accounts) => {
    let match;
    let activeCount = 0;
    for (const account of accounts ?? []) {
        const { broker, status } = account.account;
        if (broker === 'seccl') {
            if (status === 'ACTIVE') {
                activeCount += 1;
                if (activeCount > 1) {
                    return undefined;
                }
            }
            if (!match && status !== 'NOT_STARTED' && status !== 'CLOSED') {
                match = account;
            }
        }
    }
    return match;
});
export const selectActiveTradingAccountId = createSelector(selectTradingAccount, (account) => account?.account.id);
export const selectSecclAccount = createSelector(selectInvestAccounts, (accounts) => {
    const secclAccount = accounts?.find((account) => account.account.broker === 'seccl' && account.account.status === 'ACTIVE');
    return secclAccount;
});
export const selectAlpacaAccount = createSelector(selectInvestAccounts, (accounts) => getAlpacaAccount(accounts));
export const activitySelector = createSelector((state, assetId) => assetId ? state.unpersistedInvestReducer.activityCacheForAssetId?.[assetId] : undefined, (activities) => activities);
export function selectWatchlists(state) {
    return state.invest.watchlists;
}
export const selectRecurringBuys = (state) => state.unpersistedInvestReducer.recurringBuys;
export const selectIsHmrcAppInstalled = (state) => state.unpersistedInvestReducer.isHmrcAppInstalled;
/**
 * Selects the array of symbols that are top trending during the current session.
 */
export const selectTopTrending = createSelector([(state) => state.invest.topTrendingForTradingAccount, selectActiveTradingAccountId], (topTrendingForAccount, tradingAccountId) => tradingAccountId ? topTrendingForAccount[tradingAccountId]?.slice(0, 3) || [] : []);
/**
 * Selects the array of symbols that are most watched during the current session.
 */
export const selectMostWatchedStocks = (state) => state.invest.mostWatchedStocks;
/**
 * Selects the array of symbols that are most held during the current session.
 */
export const selectMostHeldStocks = (state) => state.invest.mostHeldStocks;
/**
 * Selects the array of symbols that are newly added.
 */
/**
 * Selects the array of symbols that fell down in price the most during the current session.
 */
export const selectLosers = (state) => state.invest.losers;
/**
 * Selects the array of symbols that went up in price the most during the current session.
 */
export const selectGainers = (state) => state.invest.gainers;
/**
 * Selects the watchListAssetCache from watchList reducer.
 */
export const selectWatchListAssetCache = (state) => state.watchList.watchListAssetCache;
/**
 * Selects the currentPriceCache from invest reducer.
 */
export const selectCurrentPriceCache = (state) => state.invest.currentPriceCaches;
/**
 * Selects the currentPriceChangeCache from invest reducer.
 */
export const selectPriceCache = (state) => state.invest.priceChangeCaches;
/**
 * Selects the array of AssetWithPrice that is sorted according to the selected watchList filter(AssetFilterCategory).
 */
export const selectAssetsForWatchListFilter = createSelector([
    selectWatchListAssetCache,
    (_state, watchListId, filter) => ({
        watchListId,
        filter,
    }),
    selectCurrentPriceCache,
    selectPriceCache,
], (watchListAssetCache, { filter, watchListId }, currentPrices, priceChanges) => {
    const watchListAssetsForWatchListId = watchListAssetCache?.[watchListId] || [];
    if (filter === AssetFilterCategory.NAME) {
        // watchListAssetCache is already sorted alphabetically by name
        return watchListAssetsForWatchListId;
    }
    const assets = [];
    watchListAssetsForWatchListId.forEach((asset) => {
        const currentPrice = currentPrices?.[asset.id];
        const priceChange = priceChanges?.[asset.id];
        if (currentPrice && priceChange) {
            assets.push({
                ...asset,
                sharePrice: currentPrice.value,
                sharePriceChange: priceChange.value,
                sharePriceChangePct: priceChange.percent,
            });
        }
    });
    if (filter === AssetFilterCategory.SYMBOL) {
        // sort by symbol
        assets.sort((a, b) => {
            // Push elements without a symbol to the end
            if (!a.symbol && !b.symbol) {
                return 0;
            }
            if (!a.symbol) {
                return 1;
            }
            if (!b.symbol) {
                return -1;
            }
            if (a.symbol < b.symbol) {
                return -1;
            }
            if (a.symbol > b.symbol) {
                return 1;
            }
            return 0;
        });
        return assets;
    }
    if (filter === AssetFilterCategory.PRICE) {
        // sort by price
        assets.sort((a, b) => b.sharePrice - a.sharePrice);
        return assets;
    }
    if (filter === AssetFilterCategory.PRICE_CHANGE) {
        // sort by priceChangePct
        assets.sort((a, b) => b.sharePriceChangePct - a.sharePriceChangePct);
        return assets;
    }
    assertUnreachable(filter);
    return [];
});
export const selectSlicedLearnVideos = createSelector([
    (state) => state.utils.featureFlags,
    (_state, videoIndex) => videoIndex,
    (_state, _videoIndex, category) => category,
], (featureFlags, videoIndex, category) => {
    const learnVideos = featureFlags.learn_educational_content_v2?.extra?.[category]?.videos;
    if (learnVideos?.length) {
        return learnVideos.slice(videoIndex, learnVideos.length);
    }
    return [];
});
/**
 * Selects the entire stock news cache - `{ [symbol]: News }`
 */
export const selectStockNews = (state) => state.investStockNews.cache;
/**
 * Selects array of news for given symbol, guaranteed to be unique by `id` key.
 */
export const selectStockNewsForAssetId = createSelector((state, assetId) => (assetId ? state.investStockNews.cache[assetId] : undefined), (_state, _assetId, totalNumber) => totalNumber, (news, totalNumber) => {
    if (!news) {
        return [];
    }
    const result = Array.from(new Map(news.map((newsItem) => [newsItem.id, newsItem])).values());
    return totalNumber ? result.slice(0, totalNumber) : result;
});
/**
 * Simply selects ordersCache from invest reducer.
 */
export const selectOrdersCache = (state) => state.invest.ordersCache;
export const selectPendingAndQueuedOrders = createSelector([
    selectOrdersCache,
    (state) => state.invest.pendingOrdersForTradingAccountCache,
    (state) => state.invest.queuedOrdersForTradingAccountCache,
    selectActiveTradingAccountId,
], (ordersCache, pendingOrdersForTradingAccountCache, queuedOrdersForTradingAccountCache, tradingAccountId) => {
    const queuedOrders = [];
    const pendingOrders = [];
    if (tradingAccountId) {
        const pendingOrdersForTradingAccount = pendingOrdersForTradingAccountCache[tradingAccountId];
        const queuedOrdersForTradingAccount = queuedOrdersForTradingAccountCache[tradingAccountId];
        pendingOrdersForTradingAccount?.forEach((id) => {
            const order = ordersCache[id];
            if (order) {
                if (order.status === 'pending' && order.orderType === SellOrderType.MARKET) {
                    pendingOrders.push(order);
                }
            }
        });
        queuedOrdersForTradingAccount?.forEach((id) => {
            const order = ordersCache[id];
            if (order) {
                if (order.status === 'queued' && order.orderType === SellOrderType.MARKET) {
                    queuedOrders.push(order);
                }
            }
        });
    }
    return {
        queuedOrders,
        pendingOrders,
    };
});
/**
 * Selects a boolean flag indicating that `/pending-top-ups` returned some pending topup payments.
 */
export const selectPendingTopupsFlag = (state) => Boolean(debug.emulatePendingTopups || state.invest.pendingTopupsFlag);
export const selectStaticCollections = createSelector([
    (state) => state.invest,
    (state) => state.collections,
    selectActiveTradingAccountId,
    (_state, type) => type,
], (investStore, collectionsStore, tradingAccountId, type) => {
    switch (type) {
        case 'BIGGEST_GAINERS':
            return investStore.gainers || [];
        case 'BIGGEST_LOSERS':
            return investStore.losers || [];
        case 'HIGHEST_EARNERS':
            return collectionsStore.highEarners || [];
        case 'HIGHEST_NUMBER_OF_EMPLOYEES':
            return collectionsStore.biggestEmployers || [];
        case 'HIGHEST_DIVIDEND_YIELD':
            return collectionsStore.dividendYield || [];
        case 'HIGHEST_MARKET_CAP':
            return collectionsStore.highestMarketCap || [];
        case 'MOST_WATCHED':
            return collectionsStore.mostWatched || [];
        case 'TOP_TRENDING':
            return tradingAccountId ? investStore.topTrendingForTradingAccount[tradingAccountId] || [] : [];
        case 'MOST_POPULAR':
            return investStore.mostHeldStocks || [];
        default:
            return [];
    }
});
export const selectSetupOnboardingList = createSelector([
    (_state, type) => type,
    (state) => state.invest.topTrendingForTradingAccount,
    (state) => state.invest.mostHeldStocks,
    (state) => state.invest.mostWatchedStocks,
    (state) => state.collections.highestMarketCap,
    selectActiveTradingAccountId,
], (type, topTrendingForAccount, mostHeld, mostWatchedStocks, highestMarketCap, tradingAccountId) => {
    switch (type) {
        case 'top100':
            return highestMarketCap;
        case 'mostTraded':
            return tradingAccountId ? topTrendingForAccount[tradingAccountId] || [] : undefined;
        case 'mostHeld':
            return mostHeld;
        case 'mostWatched':
            return mostWatchedStocks;
        default:
            assertUnreachable(type);
            return [];
    }
});
export const selectHasFxFeesFeature = createSelector([selectIsGBUser, selectShowSecclFlow], (isGBUser, showSecclFlow) => !!(isGBUser && !showSecclFlow));
export const selectHasAUMFeesFeature = createSelector([selectIsGBUser, selectShowSecclFlow], (isGBUser, showSecclFlow) => !!(isGBUser && showSecclFlow));
export const selectUserFxFee = createSelector(selectUserTier, (state) => state.invest.tierFxRateMap, selectHasFxFeesFeature, (tier, tierFxRateMap, hasFxFeesFeature) => {
    if (!hasFxFeesFeature) {
        return undefined;
    }
    return tierFxRateMap?.[tier];
});
export const selectHasCountryOfTaxResidence = (store) => store.user.user.countryOfTaxResidence;
export const selectMissingRequiredNationalityIdentifiers = (store) => store.invest.requiredNationalityIdentifiers;
export const selectShouldShowPortfolioBlock = (store) => !!store.invest.requiredNationalityIdentifiers?.length;
export const selectShouldAskForExtraInsuranceNumber = selectShouldShowPortfolioBlock;
export const selectAnnualIncome = createSelector((store) => store.user.userAdditionalInfo, (store) => store.invest.onboarding?.params, (userAdditionalInfo, onboardingParams) => {
    if (userAdditionalInfo.annualIncomeMin !== null &&
        userAdditionalInfo.annualIncomeMin !== undefined &&
        userAdditionalInfo.annualIncomeMax !== null &&
        userAdditionalInfo.annualIncomeMax !== undefined) {
        return {
            annualIncomeMin: userAdditionalInfo.annualIncomeMin,
            annualIncomeMax: userAdditionalInfo.annualIncomeMax,
        };
    }
    if (onboardingParams?.annualIncomeMin !== null &&
        onboardingParams?.annualIncomeMin !== undefined &&
        onboardingParams?.annualIncomeMax !== null &&
        onboardingParams?.annualIncomeMax !== undefined) {
        return {
            annualIncomeMin: onboardingParams.annualIncomeMin,
            annualIncomeMax: onboardingParams.annualIncomeMax,
        };
    }
    return undefined;
});
export const selectHasAnnualIncome = createSelector(selectAnnualIncome, (annualIncome) => !!annualIncome);
export const selectNetworth = createSelector((store) => store.user.userAdditionalInfo, (store) => store.invest.onboarding?.params, (userAdditionalInfo, onboardingParams) => {
    if (userAdditionalInfo.liquidNetWorthMin !== null &&
        userAdditionalInfo.liquidNetWorthMin !== undefined &&
        userAdditionalInfo.liquidNetWorthMax !== null &&
        userAdditionalInfo.liquidNetWorthMax !== undefined) {
        return {
            liquidNetWorthMin: userAdditionalInfo.liquidNetWorthMin,
            liquidNetWorthMax: userAdditionalInfo.liquidNetWorthMax,
        };
    }
    if (onboardingParams?.liquidNetWorthMin !== null &&
        onboardingParams?.liquidNetWorthMin !== undefined &&
        onboardingParams?.liquidNetWorthMax !== null &&
        onboardingParams?.liquidNetWorthMax !== undefined) {
        return {
            liquidNetWorthMin: onboardingParams.liquidNetWorthMin,
            liquidNetWorthMax: onboardingParams.liquidNetWorthMax,
        };
    }
    return undefined;
});
export const selectHasNetworth = createSelector(selectNetworth, (networth) => !!networth);
export const selectFundingSource = createSelector((store) => store.user.userAdditionalInfo, (store) => store.invest.onboarding?.params, (userAdditionalInfo, onboardingParams) => {
    if (userAdditionalInfo.fundingSource) {
        return userAdditionalInfo.fundingSource;
    }
    if (onboardingParams?.fundingSource) {
        return onboardingParams.fundingSource;
    }
    return undefined;
});
export const selectEmploymentStatus = createSelector((store) => store.user.userAdditionalInfo, (store) => store.invest.onboarding?.params, (userAdditionalInfo, onboardingParams) => {
    if (userAdditionalInfo.employmentStatus) {
        return userAdditionalInfo.employmentStatus;
    }
    if (onboardingParams?.employmentStatus) {
        return onboardingParams.employmentStatus;
    }
    return undefined;
});
export const selectTotalDayTradeCount = createSelector((state) => state.unpersistedInvestReducer.filledDayTrades, (state) => state.unpersistedInvestReducer.pendingDayTrades, (filledDayTrades, pendingDayTrades) => {
    if (filledDayTrades && pendingDayTrades) {
        return filledDayTrades.length + pendingDayTrades.length;
    }
    return undefined;
});
export const selectLimitStopOrders = createSelector((state) => state.invest.ordersCache, (state) => state.invest.limitStopOrderIdCache, (ordersCache, limitStopOrderIdCache) => {
    const orders = [];
    if (ordersCache && limitStopOrderIdCache?.length) {
        limitStopOrderIdCache.forEach((id) => {
            const order = ordersCache[id];
            if (order) {
                orders.push(order);
            }
        });
    }
    return orders;
});
export const selectIsRequestingOrders = createSelector([
    (state) => state.unpersistedInvestReducer.requestingLimitStopOrders,
    (state) => state.unpersistedInvestReducer.requestingLimitStopOrdersAssetId,
    (state) => state.unpersistedInvestReducer.requestingLimitStopOrdersForAssetId,
    (state) => state.unpersistedInvestReducer.requestingMarketOrders,
    (state) => state.unpersistedInvestReducer.requestingMarketOrdersAssetId,
    (state) => state.unpersistedInvestReducer.requestingMarketOrdersForAssetId,
    (_state, infoObj) => infoObj,
], (requestingLimitStopOrders, requestingLimitStopOrdersAssetId, requestingLimitStopOrdersForAssetId, requestingMarketOrders, requestingMarketOrdersAssetId, requestingMarketOrdersForAssetId, infoObj) => {
    if (infoObj.orderType === 'limitAndStop') {
        if (!infoObj.assetId) {
            return !!requestingLimitStopOrders;
        }
        if (requestingLimitStopOrdersAssetId && requestingLimitStopOrdersForAssetId) {
            if (infoObj.assetId === requestingLimitStopOrdersAssetId) {
                return requestingLimitStopOrdersForAssetId;
            }
        }
    }
    else if (infoObj.orderType === 'pending' || infoObj.orderType === 'queued') {
        if (!infoObj.assetId) {
            return !!requestingMarketOrders;
        }
        if (requestingMarketOrdersAssetId && requestingMarketOrdersForAssetId) {
            if (infoObj.assetId === requestingMarketOrdersAssetId) {
                return requestingMarketOrdersForAssetId;
            }
        }
    }
    return false;
});
export const selectFirstThreePendingAlerts = createSelector([(state) => state.unpersistedInvestReducer.pendingStockAlerts, (_state, assetId) => assetId], (pendingAlerts, assetId) => {
    if (assetId) {
        if (assetId !== ALL_STOCKS_KEY) {
            // Trying to find the array for the assetId
            const dataArr = pendingAlerts?.[assetId];
            if (dataArr?.length) {
                // return the first three if found
                return dataArr.slice(0, 3);
            }
            // trying to find the relevant data in allStocks list
            const allStocksDataArr = pendingAlerts?.[ALL_STOCKS_KEY]?.filter((stockAlert) => stockAlert.assetId === assetId);
            if (allStocksDataArr?.length) {
                // return the first three if found
                return allStocksDataArr.slice(0, 3);
            }
        }
        else {
            return pendingAlerts?.[assetId]?.slice(0, 3);
        }
    }
    return [];
});
export const selectHasPendingStockAlert = createSelector([(state) => state.unpersistedInvestReducer.pendingStockAlerts, (_state, assetId) => assetId], (pendingAlerts, assetId) => {
    if (assetId) {
        // Trying to find the array for the assetId
        const dataArr = pendingAlerts?.[assetId];
        if (dataArr?.length) {
            return true;
        }
        // trying to find the relevant data in allStocks list
        const allStocksDataArr = pendingAlerts?.[ALL_STOCKS_KEY]?.filter((stockAlert) => stockAlert.assetId === assetId);
        if (allStocksDataArr?.length) {
            return true;
        }
    }
    return false;
});
export const selectIsInitialRequestingOrders = createSelector([
    (state) => state.unpersistedInvestReducer.requestingLimitStopOrders,
    (state) => state.unpersistedInvestReducer.requestingLimitStopOrdersPaginationToken,
    (state) => state.unpersistedInvestReducer.requestingLimitStopOrdersAssetId,
    (state) => state.unpersistedInvestReducer.requestingLimitStopOrdersForAssetId,
    (state) => state.unpersistedInvestReducer.requestingLimitStopOrdersPaginationTokenForAssetId,
    (state) => state.unpersistedInvestReducer.requestingMarketOrders,
    (state) => state.unpersistedInvestReducer.requestingMarketOrdersPaginationToken,
    (state) => state.unpersistedInvestReducer.requestingMarketOrdersAssetId,
    (state) => state.unpersistedInvestReducer.requestingMarketOrdersForAssetId,
    (state) => state.unpersistedInvestReducer.requestingMarketOrdersPaginationTokenForAssetId,
    (_state, infoObj) => infoObj,
], (requestingLimitStopOrders, requestingLimitStopOrdersPaginationToken, requestingLimitStopOrdersAssetId, requestingLimitStopOrdersForAssetId, requestingLimitStopOrdersPaginationTokenForAssetId, requestingMarketOrders, requestingMarketOrdersPaginationToken, requestingMarketOrdersAssetId, requestingMarketOrdersForAssetId, requestingMarketOrdersPaginationTokenForAssetId, infoObj) => {
    if (infoObj.orderType === 'limitAndStop') {
        if (!infoObj.assetId) {
            return !!(requestingLimitStopOrders && !requestingLimitStopOrdersPaginationToken);
        }
        if (requestingLimitStopOrdersAssetId && requestingLimitStopOrdersForAssetId) {
            if (infoObj.assetId === requestingLimitStopOrdersAssetId) {
                return !!(requestingLimitStopOrdersForAssetId && !requestingLimitStopOrdersPaginationTokenForAssetId);
            }
        }
    }
    else if (infoObj.orderType === 'pending' || infoObj.orderType === 'queued') {
        if (!infoObj.assetId) {
            return !!(requestingMarketOrders && !requestingMarketOrdersPaginationToken);
        }
        if (requestingMarketOrdersAssetId && requestingMarketOrdersForAssetId) {
            if (infoObj.assetId === requestingMarketOrdersAssetId) {
                return !!(requestingMarketOrdersForAssetId && !requestingMarketOrdersPaginationTokenForAssetId);
            }
        }
    }
    return false;
});
export const selectIsRequestingTriggeredStockAlerts = createSelector([
    (state) => state.unpersistedInvestReducer.requestingTriggeredStockAlerts,
    (state) => state.unpersistedInvestReducer.triggeredStockAlertsPaginationAssetId,
    (_state, assetId) => assetId,
], (requesting, requestingAssetId, assetId) => {
    if (assetId && requestingAssetId) {
        if (assetId === requestingAssetId) {
            return requesting;
        }
    }
    return false;
});
export const selectIsPaginationTokenForOrders = createSelector([
    (state) => state.unpersistedInvestReducer.requestingLimitStopOrdersPaginationToken,
    (state) => state.unpersistedInvestReducer.requestingLimitStopOrdersAssetId,
    (state) => state.unpersistedInvestReducer.requestingLimitStopOrdersPaginationTokenForAssetId,
    (state) => state.unpersistedInvestReducer.requestingMarketOrdersPaginationToken,
    (state) => state.unpersistedInvestReducer.requestingMarketOrdersAssetId,
    (state) => state.unpersistedInvestReducer.requestingMarketOrdersPaginationTokenForAssetId,
    (_state, infoObj) => infoObj,
], (requestingLimitStopOrdersPaginationToken, requestingLimitStopOrdersAssetId, requestingLimitStopOrdersPaginationTokenForAssetId, requestingMarketOrdersPaginationToken, requestingMarketOrdersAssetId, requestingMarketOrdersPaginationTokenForAssetId, infoObj) => {
    if (infoObj.orderType === 'limitAndStop') {
        if (!infoObj.assetId) {
            return requestingLimitStopOrdersPaginationToken;
        }
        if (requestingLimitStopOrdersAssetId && infoObj.assetId === requestingLimitStopOrdersAssetId) {
            return requestingLimitStopOrdersPaginationTokenForAssetId;
        }
    }
    else if (infoObj.orderType === 'pending' || infoObj.orderType === 'queued') {
        if (!infoObj.assetId) {
            return requestingMarketOrdersPaginationToken;
        }
        if (requestingMarketOrdersAssetId && infoObj.assetId === requestingMarketOrdersAssetId) {
            return requestingMarketOrdersPaginationTokenForAssetId;
        }
    }
    return undefined;
});
export const selectIsInitialRequestingTriggeredStockAlerts = createSelector([
    (state) => state.unpersistedInvestReducer.requestingTriggeredStockAlerts,
    (state) => state.unpersistedInvestReducer.triggeredStockAlertsPaginationToken,
    (state) => state.unpersistedInvestReducer.triggeredStockAlertsPaginationAssetId,
    (_state, assetId) => assetId,
], (requesting, triggeredPaginationToken, requestingAssetId, assetId) => {
    if (assetId && requestingAssetId) {
        if (assetId === requestingAssetId) {
            // we can tell if it is initial loading when it is requesting and pagination token is undefined
            // there will not be load more calls if paginationToken is undefined
            return requesting && !triggeredPaginationToken;
        }
    }
    return false;
});
export const selectIsRequestingPendingStockAlerts = createSelector([
    (state) => state.unpersistedInvestReducer.requestingAllPendingStockAlerts,
    (state) => state.unpersistedInvestReducer.requestingPendingStockAlerts,
    (state) => state.unpersistedInvestReducer.pendingStockAlertsPaginationAssetId,
    (_state, assetId) => assetId,
], (requestingAllPendingStocks, requesting, requestingAssetId, assetId) => {
    if (assetId === ALL_STOCKS_KEY) {
        return requestingAllPendingStocks;
    }
    if (assetId && requestingAssetId) {
        if (assetId === requestingAssetId) {
            return requesting;
        }
    }
    return false;
});
export const selectIsInitialRequestingPendingStockAlerts = createSelector([
    (state) => state.unpersistedInvestReducer.requestingAllPendingStockAlerts,
    (state) => state.unpersistedInvestReducer.allPendingStockAlertsPaginationToken,
    (state) => state.unpersistedInvestReducer.requestingPendingStockAlerts,
    (state) => state.unpersistedInvestReducer.pendingStockAlertsPaginationToken,
    (state) => state.unpersistedInvestReducer.pendingStockAlertsPaginationAssetId,
    (_state, assetId) => assetId,
], (requestingAllPendingStocks, allPendingStocksPaginationToken, requesting, pendingPaginationToken, requestingAssetId, assetId) => {
    if (assetId === ALL_STOCKS_KEY) {
        return requestingAllPendingStocks && !allPendingStocksPaginationToken;
    }
    if (assetId && requestingAssetId) {
        if (assetId === requestingAssetId) {
            // we can tell if it is initial loading when it is requesting and pagination token is undefined
            // there will not be load more calls if paginationToken is undefined
            return requesting && !pendingPaginationToken;
        }
    }
    return false;
});
const emptyEvents = [];
export const getEventsNearToToday = (events, isForPortfolio) => {
    const today = new Date();
    const indexForToday = events.findIndex((event) => !isBefore(event.date, today));
    if (indexForToday !== -1) {
        const eventsLen = events.length;
        if (indexForToday === eventsLen - 1) {
            return events.slice(isForPortfolio ? -3 : -1);
        }
        if (indexForToday === eventsLen - 2) {
            return events.slice(isForPortfolio ? -3 : -2);
        }
        if (indexForToday === eventsLen - 3) {
            return events.slice(-3);
        }
        return events.slice(indexForToday, indexForToday + 3);
    }
    return isForPortfolio ? events.slice(-3) : emptyEvents;
};
export const selectInvestmentCalendarShortList = createSelector([(state) => state.unpersistedInvestReducer.investmentCalendar, (_state, key) => key], (investmentCalendar, key) => {
    const eventsForSection = investmentCalendar?.[key]?.eventsForSection;
    const allEvents = investmentCalendar?.[key]?.events;
    if (!eventsForSection && !allEvents)
        return undefined;
    return {
        canSeeMore: !!allEvents?.length,
        events: eventsForSection || emptyEvents,
    };
});
export const getActivitiesSectionedFlashlist = (activities, key) => {
    if (!activities?.length || !key) {
        return {
            flashListData: [],
            initialScrollIndex: 0,
        };
    }
    let activeDate;
    const flashListData = [];
    let initialScrollIndex;
    let lastSectionHeaderIndex;
    const currentMonth = format(new Date(), 'MMMM');
    activities.forEach((activity) => {
        let didAddNewSection = false;
        const dateToUseRaw = activity[key];
        const dateToUse = formatDateForSectionHeader(dateToUseRaw);
        if (!activeDate || dateToUse !== activeDate) {
            activeDate = dateToUse;
            addLastInSectionInfo(flashListData);
            flashListData.push({
                id: dateToUse,
                sectionTitle: dateToUse,
            });
            const dataLen = flashListData.length;
            if (currentMonth === dateToUse) {
                initialScrollIndex = dataLen > 0 ? dataLen - 1 : dataLen;
            }
            lastSectionHeaderIndex = dataLen;
            didAddNewSection = true;
        }
        if (!didAddNewSection) {
            flashListData.push(activity);
        }
        else {
            flashListData.push({ ...activity, isFirstInSection: true });
        }
    });
    addLastInSectionInfo(flashListData);
    if (initialScrollIndex === undefined) {
        initialScrollIndex = lastSectionHeaderIndex || 0;
    }
    if (flashListData.length) {
        flashListData.push({
            id: 'lastElement',
            lastElement: true,
        });
    }
    return { flashListData, initialScrollIndex };
};
const emptySectionedEvents = [];
export const selectCorporateEventsFlashListData = createSelector([(state) => state.unpersistedInvestReducer.investmentCalendar, (_state, key) => key], (investmentCalendar, key) => {
    const calendarEventObj = investmentCalendar?.[key];
    if (calendarEventObj) {
        const { flashListData: events, initialScrollIndex } = getActivitiesSectionedFlashlist(calendarEventObj.events, 'date');
        return {
            events,
            initialScrollIndex,
            dataLength: events.length,
            futureToken: calendarEventObj.futureToken,
        };
    }
    return {
        dataLength: 0,
        futureToken: '',
        events: emptySectionedEvents,
    };
});
export const selectCorporateEventsInitialLoading = createSelector([
    (state) => state.unpersistedInvestReducer.loadingPortfolioCalendar,
    (state) => state.unpersistedInvestReducer.loadingPortfolioCalendarToken,
    (state) => state.unpersistedInvestReducer.loadingCalendarForAssetId,
    (state) => state.unpersistedInvestReducer.loadingCalendarForAssetToken,
    (_state, key) => key,
], (requesting, paginationToken, assetId, paginationTokenForAssetId, selectorKey) => {
    if (selectorKey === 'portfolio') {
        return !!(requesting && !paginationToken);
    }
    if (selectorKey === assetId) {
        return !!assetId && !paginationTokenForAssetId;
    }
    return false;
});
export const selectIsLoadingCorporateEvents = createSelector([
    (state) => state.unpersistedInvestReducer.loadingPortfolioCalendar,
    (state) => state.unpersistedInvestReducer.loadingCalendarForAssetId,
    (_state, key) => key,
], (requesting, assetId, selectorKey) => {
    if (selectorKey === 'portfolio') {
        return !!requesting;
    }
    if (selectorKey === assetId) {
        return !!assetId;
    }
    return false;
});
const getTradeInfo = (quantity, amount) => `${formatNumberOfShares(quantity)} • ${formatAmount(amount, 'USD', 2)}`;
const emptyActivities = [];
export const selectCompactActivities = createNonMemoizedSelector((state, assetId) => assetId ? state.unpersistedInvestReducer.compactActivityForAssetId?.[assetId] : undefined, (activities) => activities ?? emptyActivities);
export const selectBuyAndSellPoints = createSelector([
    selectCompactActivities,
    (_state, _assetId, selectedPeriod) => selectedPeriod,
    (_state, _assetId, _selectedPeriod, points) => points,
    (_state, _assetId, _selectedPeriod, _points, points2) => points2,
], (activities, selectedPeriod, points1, points2) => {
    if (!activities || !points1.length)
        return undefined;
    const firstTimeStamp = new Date(points1[0].timeStampRaw * 60000).toISOString();
    const filteredActivities = [];
    for (const eachActivity of activities) {
        if (isAfter(eachActivity.timestamp, firstTimeStamp) || firstTimeStamp === eachActivity.timestamp) {
            if (eachActivity.type === 'buy' || eachActivity.type === 'sell') {
                filteredActivities.push(eachActivity);
            }
        }
        else {
            break;
        }
    }
    const activitiesLen = filteredActivities.length;
    if (!activitiesLen) {
        return undefined;
    }
    filteredActivities.reverse();
    const points = [...points1, ...points2];
    // filter activities according to the selectedPeriod
    // find all relevant point for each activity
    let i = 0; // pointer for activities
    let j = 0; // pointer for points
    const pointsLen = points.length;
    const buySellPointsObj = {};
    while (i < activitiesLen) {
        const activity = filteredActivities[i];
        while (j < pointsLen) {
            const point = points[j];
            const pointDate = new Date(point.timeStampRaw * 60000);
            if ((isSameDay(pointDate, activity.timestamp) && selectedPeriod !== '1D' && selectedPeriod !== '1W') ||
                isBefore(activity.timestamp, pointDate)) {
                const isBuy = activity.type === 'buy';
                if (buySellPointsObj[String(point.timeStampRaw)]) {
                    if (isBuy) {
                        buySellPointsObj[String(point.timeStampRaw)].totalBuyShares += activity.shares;
                        buySellPointsObj[String(point.timeStampRaw)].buy.push({
                            timestamp: activity.timestamp,
                            tradeInfoDesc: getTradeInfo(activity.shares, activity.price),
                        });
                    }
                    else {
                        buySellPointsObj[String(point.timeStampRaw)].totalSellShares += activity.shares;
                        buySellPointsObj[String(point.timeStampRaw)].sell.push({
                            timestamp: activity.timestamp,
                            tradeInfoDesc: getTradeInfo(activity.shares, activity.price),
                        });
                    }
                }
                else {
                    buySellPointsObj[String(point.timeStampRaw)] = {
                        x: point.x,
                        y: point.y,
                        pointTimeStamp: point.timeStampRaw,
                        totalBuyShares: isBuy ? activity.shares : 0,
                        totalSellShares: !isBuy ? activity.shares : 0,
                        buy: isBuy
                            ? [
                                {
                                    timestamp: activity.timestamp,
                                    tradeInfoDesc: getTradeInfo(activity.shares, activity.price),
                                },
                            ]
                            : [],
                        sell: !isBuy
                            ? [
                                {
                                    timestamp: activity.timestamp,
                                    tradeInfoDesc: getTradeInfo(activity.shares, activity.price),
                                },
                            ]
                            : [],
                    };
                }
                break;
            }
            else {
                j += 1;
            }
        }
        i += 1;
    }
    const buySellPoints = Object.values(buySellPointsObj).map((buySellPoint) => {
        const buyCount = buySellPoint.buy.length;
        const sellCount = buySellPoint.sell.length;
        return {
            buyCount,
            sellCount,
            x: buySellPoint.x,
            y: buySellPoint.y,
            buy: buySellPoint.buy,
            sell: buySellPoint.sell,
            pointTimeStamp: buySellPoint.pointTimeStamp,
            colorKey: buySellPoint.totalSellShares > buySellPoint.totalBuyShares ? 'negative' : 'positive',
        };
    });
    return buySellPoints;
});
export const selectShouldTriggerInvestWalkthrough = (state) => !!state.invest.firstAccountJustBecameActive;
export const selectTradingAccountList = createSelector([selectInvestAccounts], (accounts) => {
    let isSecclGIAOperating = false;
    let isSecclISAOperating = false;
    let isAlpacaAccountActiveOrClosed = false;
    let isAlpacaGIAAccountActive = false;
    const filteredAccounts = accounts?.filter((account) => {
        const accountStatus = account.account.status;
        const shouldShowInList = accountStatus === 'ACTIVE' ||
            accountStatus === 'JISA_TRANSFER_REQUIRED' ||
            accountStatus === 'IDENTITY_CHECKS_IN_PROGRESS' ||
            accountStatus === 'OPENING_IN_PROGRESS';
        if (shouldShowInList) {
            if (account.account.broker === 'seccl') {
                if (account.account.accountType === 'GIA') {
                    isSecclGIAOperating = true;
                }
                else if (account.account.accountType === 'ISA') {
                    isSecclISAOperating = true;
                }
            }
        }
        if (account.account.broker === 'alpaca' && account.account.accountType === 'GIA') {
            if (accountStatus === 'ACTIVE' || accountStatus === 'CLOSED') {
                isAlpacaAccountActiveOrClosed = true;
            }
            if (accountStatus === 'ACTIVE') {
                isAlpacaGIAAccountActive = true;
            }
        }
        return shouldShowInList;
    });
    return {
        isSecclGIAOperating,
        isAlpacaGIAAccountActive,
        accounts: filteredAccounts,
        isAlpacaAccountActiveOrClosed,
        isSecclISAOperating,
    };
});
export const selectIsTradingAccountAlpaca = createSelector([selectTradingAccount], (tradingAccount) => tradingAccount?.isAlpaca);
export const selectActiveTradingAccountBroker = createSelector(selectTradingAccount, (account) => account?.account.broker);
export const selectHasAutoInvest = createSelector([(state) => selectFeatureFlag(state, 'auto_invest_v4').value, selectTradingAccount], (featureFlag, tradingAccount) => !!featureFlag && tradingAccount && !tradingAccount.isAlpaca);
export const selectShowSetupInvestAccountInInvestScreens = createSelector(selectTradingAccount, (tradingAccount) => {
    const isActive = tradingAccount?.account.status === 'ACTIVE';
    return !isActive;
});
export const selectShowLowerFxFeeContent = createSelector(selectUserTier, selectHasFxFeesFeature, selectActiveTradingAccountBroker, (tier, hasFxFeesFeature, broker) => {
    if (broker === 'seccl' || tier === Tier.ultimate || !hasFxFeesFeature) {
        return false;
    }
    return true;
});
export const selectTradingAccountFromId = createSelector([(_state, tradingAccountId) => tradingAccountId, selectInvestAccounts], (tradingAccountId, tradingAccounts) => {
    if (!tradingAccountId)
        return undefined;
    return tradingAccounts?.find((account) => account.account.id === tradingAccountId)?.account;
});
export const selectTradingAccountObjFromId = createSelector([(_state, tradingAccountId) => tradingAccountId, selectInvestAccounts], (tradingAccountId, tradingAccounts) => {
    if (!tradingAccountId)
        return undefined;
    return tradingAccounts?.find((account) => account.account.id === tradingAccountId);
});
// Pre-select ISA if its allowance isn't fully used, then GIA, then JISA as a last resort. Seccl only.
export const selectInitialToTradingAccount = createSelector([selectInvestAccounts], (accounts) => {
    const isaAccount = getSecclISAAccount(accounts)?.account;
    if (isaAccount?.accountType === 'ISA' &&
        isaAccount.status === 'ACTIVE' &&
        (isaAccount.isaRemainingAllowanceAfterPending?.value ?? 0) > 0) {
        return isaAccount;
    }
    const giaAccount = getSecclGiaAccount(accounts)?.account;
    if (giaAccount && giaAccount.status === 'ACTIVE') {
        return giaAccount;
    }
    const jisaAccounts = getSecclJISAAccounts(accounts);
    const jisaWithAllowance = jisaAccounts?.find((account) => account.account.accountType === 'JISA' &&
        account.account.status === 'ACTIVE' &&
        (account.account.jisaRemainingAllowanceAfterPending?.value ?? 0) > 0);
    return (jisaWithAllowance ?? jisaAccounts?.[0])?.account;
});
const emptyTradingAccountObject = [];
export const selectActiveTradingSecclAccounts = createSelector([selectInvestAccounts], (accounts) => accounts?.filter((account) => account.account.broker === 'seccl' && account.account.status === 'ACTIVE') ||
    emptyTradingAccountObject);
const accountTypeSortOrder = {
    ISA: 0,
    GIA: 1,
    JISA: 2,
};
export const selectActiveTradingSecclAccountsSorted = createSelector([selectActiveTradingSecclAccounts], (accounts) => [...accounts].sort((a, b) => {
    const orderDiff = accountTypeSortOrder[a.account.accountType] - accountTypeSortOrder[b.account.accountType];
    if (orderDiff !== 0)
        return orderDiff;
    if (a.account.accountType === 'JISA' && b.account.accountType === 'JISA') {
        const aName = `${a.account.child.firstName} ${a.account.child.surname}`;
        const bName = `${b.account.child.firstName} ${b.account.child.surname}`;
        return aName.localeCompare(bName);
    }
    return 0;
}));
export const selectActiveTradingSecclAccountsLength = createSelector([selectActiveTradingSecclAccounts], (accounts) => accounts.length);
export const selectIsAnyTradingAccountActive = createSelector([selectInvestAccounts], (accounts) => !!accounts?.find((account) => account.account.status === 'ACTIVE'));
export const selectIsEnabledTradingAccountActive = createSelector([selectInvestAccounts, selectShowSecclFlow], (accounts, showSecclFlow) => {
    if (showSecclFlow) {
        const secclAccount = accounts?.some((account) => account.account.broker === 'seccl' && account.account.status === 'ACTIVE');
        return !!secclAccount;
    }
    const alpacaAccount = getAlpacaAccount(accounts);
    return alpacaAccount?.account.status === 'ACTIVE';
});
export const selectHasStartedAnyTradingAccount = createSelector([selectInvestAccounts], (accounts) => {
    if (!accounts) {
        return false;
    }
    return !!accounts.find((account) => account.account.status !== 'NOT_STARTED');
});
export const selectFirstThreePendingActivities = createSelector([(state) => state.unpersistedInvestReducer.pendingActivitiesForTradingAccount, selectActiveTradingAccountId], (pendingActivitiesForTradingAccount, tradingAccountId) => {
    const pendingActivities = (tradingAccountId ? pendingActivitiesForTradingAccount?.[tradingAccountId] : []) || [];
    const activities = pendingActivities.slice(0, 3);
    return {
        activities,
        activityLength: activities?.length || 0,
        canSeeAll: !!(pendingActivities && pendingActivities?.length > 3),
    };
});
export const selectInitialInvestmentActivity = createSelector([(state) => state.unpersistedInvestReducer.activityCacheForTradingAccount, selectActiveTradingSecclAccounts], (activityCacheForTradingAccount, activeSecclAccounts) => {
    if (activeSecclAccounts.length !== 1)
        return undefined;
    const activities = activityCacheForTradingAccount?.[activeSecclAccounts[0].account.id];
    // Only the initial investment if it is the account's only activity
    if (activities?.length !== 1)
        return undefined;
    const [activity] = activities;
    if (activity.type !== 'buy')
        return undefined;
    return activity.status === 'pending' ? activity : undefined;
});
export const selectPortfolioTotalBalance = createSelector([(store) => store.invest.tradingAccountPositionsCache, selectActiveTradingAccountId], (tradingAccountPositionsCache, tradingAccountId) => {
    const positionsCache = tradingAccountId ? tradingAccountPositionsCache?.[tradingAccountId] : undefined;
    const positions = positionsCache ? Object.values(positionsCache) : [];
    return {
        value: positions.reduce((prev, curr) => prev + (curr?.amount?.value || 0), 0),
        currency: 'GBP',
    };
});
export const selectTradingAccountHasPositions = createSelector([
    (store) => store.invest.tradingAccountPositionsCache,
    (_store, tradingAccountId) => tradingAccountId,
], (tradingAccountPositionsCache, tradingAccountId) => {
    const positionsCache = tradingAccountId ? tradingAccountPositionsCache?.[tradingAccountId] : undefined;
    const positions = positionsCache ? Object.values(positionsCache).filter(Boolean) : [];
    return !!positions.length;
});
export const selectPositions = createSelector([
    (state) => state.invest.tradingAccountPositionsCache,
    (state) => state.invest.caches,
    (state) => state.invest.categoryIdSectorMap,
    selectActiveTradingAccountId,
], (tradingAccountPositionsCache, cache, categoryIdSectorMap, tradingAccountId) => {
    const positionsCache = (tradingAccountId ? tradingAccountPositionsCache?.[tradingAccountId] : {}) || {};
    const assetIds = Object.keys(positionsCache);
    filterDataByKey(assetIds, positionsCache, 'amount');
    const result = [];
    assetIds.forEach((assetId) => {
        const stockMeta = cache[assetId];
        const position = positionsCache[assetId];
        // the following condition will always return true since we are looping over keys of positionsCache
        if (position) {
            result.push({
                assetId,
                symbol: stockMeta?.symbol,
                name: stockMeta?.name,
                iconUrl: stockMeta?.iconUrl,
                quantity: position.amount.value,
                categoryId: position.categoryId,
                sector: position.categoryId && categoryIdSectorMap
                    ? categoryIdSectorMap[position.categoryId]?.name || 'Others'
                    : 'Others',
                availableToSell: position.availableToSell,
                amount: position.amount,
            });
        }
    });
    return result;
});
export const selectPositionsForTradingAccount = createSelector([
    (state) => state.invest.tradingAccountPositionsCache,
    (state) => state.invest.caches,
    (state) => state.invest.categoryIdSectorMap,
    (state, tradingAccountId) => tradingAccountId ?? selectActiveTradingAccountId(state),
], (tradingAccountPositionsCache, cache, categoryIdSectorMap, tradingAccountId) => {
    const positionsCache = (tradingAccountId ? tradingAccountPositionsCache?.[tradingAccountId] : {}) || {};
    const assetIds = Object.keys(positionsCache);
    filterDataByKey(assetIds, positionsCache, 'amount');
    const result = [];
    assetIds.forEach((assetId) => {
        const stockMeta = cache[assetId];
        const position = positionsCache[assetId];
        // the following condition will always return true since we are looping over keys of positionsCache
        if (position) {
            result.push({
                assetId,
                symbol: stockMeta?.symbol,
                name: stockMeta?.name,
                iconUrl: stockMeta?.iconUrl,
                quantity: position.amount.value,
                categoryId: position.categoryId,
                sector: position.categoryId && categoryIdSectorMap
                    ? categoryIdSectorMap[position.categoryId]?.name || 'Others'
                    : 'Others',
                availableToSell: position.availableToSell,
                amount: position.amount,
            });
        }
    });
    return result;
});
export const selectSellablePositionsForTradingAccount = createSelector([selectPositionsForTradingAccount], (positions) => positions.filter((position) => position.availableToSell > 0));
export const selectFundInfo = createSelector([(state) => state.unpersistedInvestReducer.secclAssets], (secclAssets) => {
    if (!secclAssets) {
        return [];
    }
    // Group assets by fundTheme
    const groupedByTheme = secclAssets.reduce((acc, asset) => {
        if (asset.fundTheme) {
            if (!acc[asset.fundTheme]) {
                acc[asset.fundTheme] = [];
            }
            acc[asset.fundTheme].push(asset);
        }
        return acc;
    }, {});
    // Map to the desired structure in the order of fundThemeInfoMap
    return Object.keys(fundThemeInfoMap)
        .filter((fundTheme) => groupedByTheme[fundTheme] && groupedByTheme[fundTheme].length > 0)
        .map((fundTheme) => {
        const themeInfo = fundThemeInfoMap[fundTheme];
        const fundAssets = groupedByTheme[fundTheme];
        return {
            fundInfo: {
                icon: themeInfo.icon,
                title: themeInfo.title,
                titleWidth: themeInfo.width,
                colorKey: themeInfo.colorKey,
                totalNumber: fundAssets.length,
            },
            fundAssets: fundAssets.filter((fundAsset) => typeof fundAsset.sharePrice === 'number'),
        };
    });
});
export const selectGeoInfo = createSelector([(state) => state.unpersistedInvestReducer.secclAssets], (secclAssets) => {
    if (!secclAssets) {
        return [];
    }
    // Group assets by fundGeo
    const groupedByGeo = secclAssets.reduce((acc, asset) => {
        if (asset.fundGeo) {
            if (!acc[asset.fundGeo]) {
                acc[asset.fundGeo] = [];
            }
            acc[asset.fundGeo].push(asset);
        }
        return acc;
    }, {});
    // Map to the desired structure in the order of fundGeoInfoMap
    return Object.keys(fundGeoInfoMap)
        .filter((fundGeo) => groupedByGeo[fundGeo] && groupedByGeo[fundGeo].length > 0)
        .map((fundGeo) => {
        const geoInfo = fundGeoInfoMap[fundGeo];
        const fundAssets = groupedByGeo[fundGeo];
        return {
            fundInfo: {
                title: geoInfo.title,
                imgUrl: geoInfo.imgUrl,
                titleWidth: geoInfo.width,
                subTitle: geoInfo.subTitle,
                totalNumber: fundAssets.length,
            },
            fundAssets: fundAssets.filter((fundAsset) => typeof fundAsset.sharePrice === 'number'),
        };
    });
});
export const selectTradingAccountActivitiesInitialLoading = createSelector([
    (_state, key) => key,
    (state) => state.unpersistedInvestReducer.requestingActivitiesForTradingAccount,
    (state) => state.unpersistedInvestReducer.requestingActivitiesPaginationTokenForTradingAccount,
], (key, requesting, paginationTokenObj) => !!(requesting?.[key] && paginationTokenObj?.[key] === undefined));
export const selectTradingAccountActivitySections = createSelector([
    (_state, genericType) => genericType,
    selectActiveTradingAccountId,
    (state) => state.unpersistedInvestReducer.activityCacheForTradingAccount,
    (state) => state.unpersistedInvestReducer.orderActivityCacheForTradingAccount,
    (state) => state.unpersistedInvestReducer.transactionActivityCacheForTradingAccount,
    (state) => state.unpersistedInvestReducer.interestActivityCacheForTradingAccount,
    (state) => state.unpersistedInvestReducer.dividendActivityCacheForTradingAccount,
], (genericType, tradingAccountId, activityCacheForTradingAccount, orderActivityCacheForTradingAccount, transactionActivityCacheForTradingAccount, interestActivityCacheForTradingAccount, dividendActivityCacheForTradingAccount) => {
    if (tradingAccountId) {
        const cache = (() => {
            switch (genericType) {
                case 'Orders':
                    return orderActivityCacheForTradingAccount;
                case 'Transactions':
                    return transactionActivityCacheForTradingAccount;
                case 'Interest':
                    return interestActivityCacheForTradingAccount;
                case 'Dividends':
                    return dividendActivityCacheForTradingAccount;
                default:
                    return activityCacheForTradingAccount;
            }
        })();
        const activitiesRaw = cache?.[tradingAccountId];
        if (activitiesRaw?.length) {
            const activities = [];
            const pendingActivities = [];
            activitiesRaw.forEach((activity) => {
                if (activity) {
                    if ('status' in activity && activity.status === 'pending') {
                        pendingActivities.push(activity);
                    }
                    else {
                        activities.push(activity);
                    }
                }
            });
            const activitySections = activities?.length
                ? getActivitiesSectionedFlashlistFromUtils(activities, 'timestamp')
                : [];
            const pendingActivitySections = pendingActivities?.length
                ? [
                    {
                        id: 'Pending',
                        sectionTitle: 'Pending',
                    },
                    ...addFlashListSectionedDataToActivities(pendingActivities),
                ]
                : [];
            return [...pendingActivitySections, ...activitySections];
        }
    }
    return [];
});
export const selectShouldShowMigrationToSecclBanner = createSelector([selectInvestAccounts], (tradingAccounts) => {
    const alpacaAccount = getAlpacaAccount(tradingAccounts);
    const secclISAAccount = getSecclISAAccount(tradingAccounts);
    const secclAccount = getSecclGiaAccount(tradingAccounts);
    const alpacaAccountStatus = alpacaAccount?.account.status;
    const secclAccountStatus = secclAccount?.account.status;
    const secclISAAccountStatus = secclISAAccount?.account.status;
    if ((alpacaAccountStatus === 'ACTIVE' || alpacaAccountStatus === 'CLOSED') &&
        secclAccountStatus === 'NOT_STARTED' &&
        secclISAAccountStatus === 'NOT_STARTED') {
        return true;
    }
    return false;
});
export const selectAlpacaAccountStatus = createSelector([selectInvestAccounts], (tradingAccounts) => {
    const alpacaAccount = getAlpacaAccount(tradingAccounts);
    return alpacaAccount?.account.status;
});
export const selectSecclAccountStatus = createSelector([selectInvestAccounts], (tradingAccounts) => {
    const secclAccount = getSecclGiaAccount(tradingAccounts);
    return secclAccount?.account.status;
});
export const selectAlpacaAccountClosingDate = createSelector([(store) => selectFeatureFlag(store, 'seccl_onboarding_v3')], (flag) => flag.extra?.closingDate);
export const selectAlpacaAccountClosingLongDate = createSelector([(store) => selectFeatureFlag(store, 'seccl_onboarding_v3')], (flag) => flag.extra?.closingLongDate);
export const selectAlpacaAccountClosingInfo = createSelector([(store) => selectFeatureFlag(store, 'seccl_onboarding_v3')], (flag) => flag.extra?.closingInfo);
export const selectAlpacaAccountClosingInfoIfSecclAccountActive = createSelector([(store) => selectFeatureFlag(store, 'seccl_onboarding_v3')], (flag) => flag.extra?.closingInfoIfSecclAccountActive);
export const selectSecclGiaReadyMadeFundsAssetIds = createSelector((store) => store.utils.featureFlags, (featureFlags) => {
    const ids = featureFlags?.seccl_gia_ready_made_funds?.extra?.ids;
    return ids;
});
export const selectSecclGiaReadyMadeFunds = createSelector(selectSecclGiaReadyMadeFundsAssetIds, (store) => store.unpersistedInvestReducer.secclAssets, (ids, secclAssets) => {
    if (ids?.length && secclAssets?.length) {
        const secclAssetIds = new Set(secclAssets.map((asset) => asset.id));
        return ids.filter((id) => secclAssetIds.has(id));
    }
    return [];
}, {
    memoizeOptions: {
        resultEqualityCheck: (a, b) => isEqual(a, b),
    },
});
export const selectCachedProductTransfer = (state, id) => state.unpersistedInvestReducer.productTransferCache?.[id];
export const selectTransferProviders = createSelector([
    (state, accountType, type) => state.unpersistedInvestReducer.transferProvidersCache?.[`${accountType}-${type}`],
], (entry) => ({
    providers: entry?.providers ?? [],
    isFetching: entry?.isFetching ?? false,
}), {
    memoizeOptions: {
        resultEqualityCheck: (a, b) => isEqual(a, b),
    },
});
export const selectHasTradedInAnySecclAccount = createSelector([selectInvestAccounts], (accounts) => !!accounts?.some((account) => account.account.broker === 'seccl' && account.account.status === 'ACTIVE' && account.hasTraded));
export const selectActiveSecclAccounts = createSelector([selectInvestAccounts], (accounts) => accounts?.filter((account) => account.account.broker === 'seccl' && account.account.status === 'ACTIVE'), {
    memoizeOptions: {
        resultEqualityCheck: (a, b) => isEqual(a, b),
    },
});
export const selectShouldShowInvestItemsInWalkthroughV6 = createSelector([
    selectInvestAccounts,
    (state) => state.invest.onboarding,
    (state) => state.user.user.kycStartedAt,
    (state) => state.pots.account?.status,
    (state) => state.deviceSettings.hasTriedSettingsUpInvest,
], (accounts, onboardingState, kycStartedAt, potsAccountStatus, hasTriedSettingsUpInvest) => accounts &&
    !accounts?.some((account) => account.account.status !== 'NOT_STARTED') &&
    (!hasTriedSettingsUpInvest || (!onboardingState && !kycStartedAt && potsAccountStatus === 'NOT_STARTED')));
export const selectShowISATransfers = createSelector([(store) => store.utils.featureFlags], (featureFlags) => checkFeatureFlag(featureFlags, 'isa_transfer'));
export const selectAutoInvestPlans = (state) => state.invest.autoInvestPlans;
export const selectAutoInvestPlanForAccount = (state, accountId) => accountId ? state.invest.autoInvestPlans?.[accountId] : undefined;
export const selectAutoInvestAllocations = createSelector((state) => {
    const accountId = selectActiveTradingAccountId(state);
    return accountId ? state.invest.autoInvestPlans?.[accountId] : undefined;
}, (plan) => {
    if (!plan)
        return undefined;
    return plan.allocations.map((a) => ({
        assetId: a.assetId,
        percentage: a.percentage * 100,
    }));
});
export const selectAutoInvestActivitySectionsForAccount = createSelector([
    (_, accountId) => accountId,
    (store) => store.unpersistedInvestReducer.autoInvestActivitiesForAccount,
], (accountId, autoInvestActivitiesForAccount) => {
    if (accountId) {
        const activities = autoInvestActivitiesForAccount?.[accountId];
        if (activities?.length) {
            const pendingActivities = [];
            const upcomingActivities = [];
            const regularActivities = [];
            activities?.forEach((activity) => {
                if (activity.status === 'skipped' && activity.initiationEta && isWithinNext24Hours(activity.initiationEta)) {
                    upcomingActivities.push(activity);
                }
                else if (activity.status === 'pending') {
                    pendingActivities.push(activity);
                }
                else if (activity.status === 'upcoming') {
                    upcomingActivities.push(activity);
                }
                else {
                    regularActivities.push(activity);
                }
            });
            const activitySection = regularActivities?.length
                ? [
                    {
                        id: 'Activity',
                        sectionTitle: 'Activity',
                    },
                    ...addFlashListSectionedDataToActivities(regularActivities),
                ]
                : [];
            const pendingSection = pendingActivities.length
                ? [
                    {
                        id: 'Pending',
                        sectionTitle: 'Pending',
                    },
                    ...pendingActivities,
                ]
                : [];
            const upcomingSection = upcomingActivities.length
                ? [
                    {
                        id: 'Upcoming',
                        sectionTitle: 'Upcoming',
                    },
                    ...upcomingActivities,
                ]
                : [];
            return [...upcomingSection, ...pendingSection, ...activitySection];
        }
    }
    return undefined;
});
export const selectAutoInvestStatusObj = createSelector((_state, accountId) => accountId, (state, accountId) => (accountId ? state.invest.autoInvestPlans?.[accountId] : undefined), (state) => state.invest.autoInvestFundingAccount, (state) => state.invest.connectedAccount, (accountId, plan, autoInvestFundingAccount, connectedAccount) => {
    if (!plan || !accountId || plan.status === 'cancelled')
        return undefined;
    const displayStatus = getAutoInvestDisplayStatus(plan.status, plan.directDebitMandateActive, connectedAccount && autoInvestFundingAccount
        ? autoInvestFundingAccount.sortCode?.replace(/\D/g, '') !== connectedAccount?.ukSortCode?.replace(/\D/g, '') ||
            autoInvestFundingAccount.accountNumber !== connectedAccount?.ukAccountNumber
        : false);
    return {
        displayStatus,
        isActive: displayStatus === 'Working',
        isPaused: displayStatus === 'Paused',
        isPending: displayStatus === 'Pending',
        isActionRequired: displayStatus === 'DIRECT_DEBIT_CANCELLED' || displayStatus === 'DIRECT_DEBIT_ACCOUNT_CHANGED'
            ? displayStatus
            : undefined,
    };
}, { memoizeOptions: { resultEqualityCheck: isEqual } });
export const selectAutoinvestActivitiesInitialLoading = createSelector([
    (_state, accountId) => accountId,
    (state) => state.unpersistedInvestReducer.requestingAutoinvestActivitiesForTradingAccount,
    (state) => state.unpersistedInvestReducer.requestingAutoinvestActivitiesPaginationTokenForTradingAccount,
], (accountId, requesting, paginationTokenObj) => accountId ? !!(requesting?.[accountId] && paginationTokenObj?.[accountId] === undefined) : false);
export const selectHasAssetSwitch = createSelector([(state) => selectFeatureFlag(state, 'asset_switch_v2').value], (featureFlag) => !!featureFlag);
export const selectLatestAutoinvestActivity = createSelector([(state) => state.unpersistedInvestReducer.autoInvestActivitiesForAccount, selectActiveTradingAccountId], (autoInvestActivitiesForAccount, tradingAccountId) => {
    if (tradingAccountId) {
        const activities = autoInvestActivitiesForAccount?.[tradingAccountId];
        if (activities?.length) {
            const latestActivity = activities[0];
            return latestActivity;
        }
    }
    return undefined;
});
export const selectUnopenedInvestmentAccounts = createSelector(selectInvestAccounts, selectIsGBUser, (accounts, isGBUser) => {
    if (accounts?.length && isGBUser) {
        const investmentAccounts = [];
        let hasOpenedGIA = false;
        let hasOpenedISA = false;
        accounts?.forEach((account) => {
            if (account.account.broker === 'seccl') {
                if (account.account.accountType === 'GIA' && account.account.status !== 'NOT_STARTED') {
                    hasOpenedGIA = true;
                }
                else if (account.account.accountType === 'ISA' && account.account.status !== 'NOT_STARTED') {
                    hasOpenedISA = true;
                }
            }
        });
        if (!hasOpenedGIA) {
            investmentAccounts.push({
                id: 'GIA',
                accountType: 'GIA',
                investAccountName: 'General Investments',
            });
        }
        if (!hasOpenedISA) {
            investmentAccounts.push({
                id: 'ISA',
                accountType: 'ISA',
                investAccountName: 'Stocks & Shares ISA',
            });
        }
        const firstInvestmentAccount = investmentAccounts.shift();
        if (firstInvestmentAccount) {
            investmentAccounts.unshift({
                ...firstInvestmentAccount,
                isFirst: true,
            });
        }
        const lastInvestmentAccount = investmentAccounts.pop();
        if (lastInvestmentAccount) {
            investmentAccounts.push({
                ...lastInvestmentAccount,
                isLast: true,
            });
        }
        return investmentAccounts;
    }
    return [];
}, {
    memoizeOptions: {
        resultEqualityCheck: (a, b) => isEqual(a, b),
    },
});
export const selectAutoInvestFee = createSelector((store) => store.invest.autoInvestTierFees, selectUserTier, (autoInvestTierFees, tier) => autoInvestTierFees?.[tier]);
export const selectAutoinvestAccountsThatNeedsFix = createSelector((store) => store.invest.autoInvestPlans, (autoInvestPlans) => {
    if (!autoInvestPlans)
        return undefined;
    return Object.values(autoInvestPlans).filter((plan) => !!plan && plan.status === 'active' && !plan.directDebitMandateActive);
});
export const selectSecclAccountAER = createSelector([
    (_, secclAccountType) => secclAccountType,
    (store) => selectFeatureFlag(store, 'trading_account_info'),
    selectUserTier,
], (secclAccountType, flag, tier) => secclAccountType
    ? flag.extra?.secclFees?.[secclAccountType]?.[tier]
    : undefined);
export const selectHideFundingAccountAndAutoinvestFromInvestOnboarding = createSelector((store) => store.utils.featureFlags, (featureFlags) => checkFeatureFlag(featureFlags, 'hide_funding_account_and_autoinvest_from_invest_onboarding'));
export const selectPotsOnboardingState = createSelector([(state) => state.pots.onboarding, (state) => state.invest.onboarding], (potsOnboardingState, investOnboardingState) => {
    const state = {
        ...potsOnboardingState,
        ...investOnboardingState?.params,
        ...(investOnboardingState?.screen && { screen: investOnboardingState?.screen }),
    };
    return Object.keys(state).length ? state : undefined;
});
export const selectHasOnboardedInvest = createSelector([selectInvestAccounts], (accounts) => {
    if (accounts?.length) {
        return accounts.some((account) => account.account.status !== 'NOT_STARTED');
    }
    return false;
});
export const selectAutoinvestMinAmount = createSelector([(store) => store.utils.featureFlags], (featureFlags) => {
    const minAmountFromFlag = featureFlags.min_autoinvest_amount?.extra?.minAmount;
    return checkFeatureFlag(featureFlags, 'min_autoinvest_amount') &&
        typeof minAmountFromFlag === 'number' &&
        minAmountFromFlag > 0
        ? minAmountFromFlag
        : MINIMUM_AUTOINVEST_AMOUNT;
});
